import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type {
  Baseline,
  ProjectMarketEstimates,
} from '@shared/models/project.model';
import { ProductElementOverrideTarget } from '@shared/models/project.model';
import { getElementCategoryById } from '@shared/templates/categories/template-category.helpers';
import { getProductName } from '@shared/helpers/product.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import { getRecipeScopedParentElementId } from '@shared/helpers/recipe.helpers';
import {
  elementQuantityDisplayNameRecord,
  type ElementQuantityName,
} from '@shared/models/element-quantities.model';
import {
  FORMULA_UPDATED_CHANGE_DISPLAY_VALUE,
  formatChangeNumericDisplayString,
  formatElementPropertyCountChangeDisplay,
  formatPropertyFormattedDisplayValue,
  formatQuantityFormattedDisplayValue,
} from '@shared/helpers/actions/change-value-display.helpers';
import { ELEMENT_PERCENT_PROPERTY_ENTRIES } from '@shared/helpers/actions/percent-element-property-display.helpers';
import { getCarbonProductOverrideUiSlice } from '@shared/helpers/actions/product-element-override-display.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

const finiteNumberFromParsedQuantityText = (
  raw: string,
): number | undefined => {
  const n = parseFloat(raw.trim().replace(/\s/g, '').replace(',', '.'));
  return guards.is.finiteNumber(n) ? n : undefined;
};

const quantityDisplayName = (name: string): string =>
  elementQuantityDisplayNameRecord[name as ElementQuantityName] ?? name;

/**
 * Fallback UI labels for element property technical names when the element has
 * no displayName on the property definition. Sentence-case nouns for action titles
 * are derived from these labels.
 */
export const ELEMENT_PROPERTY_DISPLAY_NAME_RECORD: Partial<
  Record<string, string>
> = {
  ...Object.fromEntries(
    Object.entries(ELEMENT_PERCENT_PROPERTY_ENTRIES).map(
      ([key, { displayLabel }]) => [key, displayLabel],
    ),
  ),
  co2e_transport: 'Transport',
  co2e_waste_percent: 'Waste',
  co2e_waste: 'Waste',
};

/**
 * Human-readable label for a known technical property name, or the unchanged
 * technical name when unknown.
 */
export const resolveKnownPropertyDisplayName = (
  technicalName: string,
): string =>
  ELEMENT_PROPERTY_DISPLAY_NAME_RECORD[technicalName] ?? technicalName;

const propertyActionNounFromDisplayLabel = (displayLabel: string): string =>
  displayLabel.length === 0
    ? displayLabel
    : displayLabel.charAt(0).toLowerCase() + displayLabel.slice(1);

/** Sentence-case nouns for {@link describeChange} / action titles. */
const PROPERTY_NOUN_MAP: Partial<Record<string, string>> = Object.fromEntries(
  Object.entries(ELEMENT_PROPERTY_DISPLAY_NAME_RECORD).flatMap(
    ([key, label]) =>
      label !== undefined
        ? [[key, propertyActionNounFromDisplayLabel(label)]]
        : [],
  ),
);

/**
 * Returns the 1-based creation order of an {@link Action} within a version's
 * `actions` array. Returns `0` when the action is not found.
 */
export const getActionNumber = (actionId: string, version: Baseline): number =>
  (version.actions?.findIndex((a) => a.id === actionId) ?? -1) + 1;

/**
 * Returns the element id a single {@link ProjectChange} targets, or `undefined`
 * for version-level changes that don't target an individual element.
 *
 * - Changes with `elementId` (most change types): that element is affected.
 * - Changes with `parentId` (e.g. {@link ProjectChangeType.ElementAdd}): the parent is affected.
 */
export const getChangeAffectedElementId = (
  change: ProjectChange,
): string | undefined => {
  if ('elementId' in change) return change.elementId;
  return undefined;
};

/**
 * Host element for per-action KPI attribution — recipe-scoped children roll up
 * to their parent host ({@link getRecipeScopedParentElementId}).
 */
export const getChangeAttributionHostElementId = (
  change: ProjectChange,
): string | undefined => {
  const elementId = getChangeAffectedElementId(change);
  if (!elementId) return undefined;
  return getRecipeScopedParentElementId(elementId) ?? elementId;
};

/**
 * Extracts unique element IDs from a list of changes.
 * Skips version-level changes that don't target individual elements.
 *
 * @see getChangeAffectedElementId
 */
export const getAffectedElementIds = (changes: ProjectChange[]): string[] => {
  const ids = new Set<string>();
  for (const change of changes) {
    const id = getChangeAffectedElementId(change);
    if (id) ids.add(id);
  }
  return [...ids];
};

const CHANGE_TYPE_LABELS: Record<ProjectChangeType, string> = {
  [ProjectChangeType.ElementPropertyResolved]: 'Property',
  [ProjectChangeType.ElementPropertyExpression]: 'Expression',
  [ProjectChangeType.ElementPropertyFormatted]: 'Formatting',
  [ProjectChangeType.ElementPropertyValue]: 'Property value override',
  [ProjectChangeType.ElementQuantityResolved]: 'Quantity',
  [ProjectChangeType.ElementQuantityExpression]: 'Quantity expression',
  [ProjectChangeType.ElementQuantityFallbackExpression]:
    'Quantity fallback expression',
  [ProjectChangeType.ElementQuantityFormatted]: 'Quantity formatting',
  [ProjectChangeType.ElementQuantityValue]: 'Quantity value override',
  [ProjectChangeType.ElementQuantityCount]: 'Quantity count',
  [ProjectChangeType.ElementQuantityFallbackCount]: 'Quantity fallback count',
  [ProjectChangeType.ElementPropertyFallbackCount]: 'Fallback count',
  [ProjectChangeType.ElementPropertyCount]: 'Count',
  [ProjectChangeType.ElementPropertyRename]: 'Rename property',
  [ProjectChangeType.ElementDataTablePropertiesApply]: 'Data table',
  [ProjectChangeType.ElementQuantitySelect]: 'Quantity unit',
  [ProjectChangeType.ElementCategoryEnrich]: 'Category enrich',
  [ProjectChangeType.ElementQuantityAutoUpdate]: 'Quantity auto-update',
  [ProjectChangeType.ElementDeactivate]: 'Deactivate',
  [ProjectChangeType.ElementActivate]: 'Activate',
  [ProjectChangeType.ElementSoftDelete]: 'Delete',
  [ProjectChangeType.ElementRecipeApply]: 'Apply recipe',
  [ProjectChangeType.ElementRecipeDetach]: 'Detach recipe',
  [ProjectChangeType.ElementRecipeClear]: 'Clear recipe',
  [ProjectChangeType.ElementCategory]: 'Category',
  [ProjectChangeType.ElementAdd]: 'Add element',
  [ProjectChangeType.ElementPropertyAdd]: 'Add property',
  [ProjectChangeType.ElementPropertyRemove]: 'Remove property',
  [ProjectChangeType.ElementName]: 'Rename element',
  [ProjectChangeType.ElementOrigin]: 'Import mapping',
  [ProjectChangeType.ElementRemove]: 'Remove element',
  [ProjectChangeType.ProductElementCountResolved]: 'Product count',
  [ProjectChangeType.ProductElementCountExpression]: 'Product expression',
  [ProjectChangeType.ProductElementCountFormatted]: 'Product formatting',
  [ProjectChangeType.ProductElementProductId]: 'Product switch',
  [ProjectChangeType.ProductElementEpd]: 'EPD',
  [ProjectChangeType.ProductElementTransportOverride]: 'Transport',
  [ProjectChangeType.ProductElementWasteOverride]: 'Spill',
  [ProjectChangeType.ProductElementCarbonLevelOverride]: 'Carbon factor',
  [ProjectChangeType.ProposalResultsRecord]: 'Results',
  [ProjectChangeType.VersionAdd]: 'Add baseline',
  [ProjectChangeType.VersionRemove]: 'Delete baseline',
  [ProjectChangeType.VersionProducts]: 'Products',
  [ProjectChangeType.VersionResults]: 'Baseline results',
  [ProjectChangeType.VersionName]: 'Rename baseline',
  [ProjectChangeType.VersionImports]: 'Element import',
  [ProjectChangeType.VersionClearProposals]: 'Clear proposals',
  [ProjectChangeType.ProposalAdd]: 'Add proposal',
  [ProjectChangeType.ProposalDuplicate]: 'Duplicate proposal',
  [ProjectChangeType.ProposalDelete]: 'Delete proposal',
  [ProjectChangeType.ProposalUpdate]: 'Update proposal',
  [ProjectChangeType.ProposalAppendAction]: 'Append proposal action',
  [ProjectChangeType.ProposalReorder]: 'Reorder proposals',
  [ProjectChangeType.ActionRename]: 'Rename action',
  [ProjectChangeType.ActionDelete]: 'Delete action',
};

/**
 * Human-readable label for a {@link ProjectChangeType}.
 */
export const getChangeTypeLabel = (type: ProjectChangeType): string =>
  CHANGE_TYPE_LABELS[type] ?? type;

/**
 * Summarizes the distinct change types in a list as human-readable labels.
 */
export const summarizeChangeTypes = (changes: ProjectChange[]): string[] => {
  const seen = new Set<ProjectChangeType>();
  for (const change of changes) {
    seen.add(change.type);
  }
  return [...seen].map(getChangeTypeLabel);
};

/**
 * Returns a short, human-readable description of the value carried by a
 * {@link ProjectChange}. Examples:
 *  - `thickness = 200`
 *  - `area_top = 12.5`
 *  - `Concrete CEM I` (product / category names when {@link version} is provided)
 *
 * Returns `undefined` for change types that have no meaningful payload to
 * display (e.g. {@link ProjectChangeType.ElementActivate} where the affected
 * element is shown elsewhere).
 *
 * @param change - The change whose value to describe
 * @param version - Optional version used to resolve product names via
 *                  {@link getProductName}; falls back to raw IDs when omitted
 * @param options - Optional context (e.g. {@link Project.marketEstimates} for carbon override display)
 */
export type GetChangeValueLabelOptions = {
  marketEstimates?: ProjectMarketEstimates;
};

export const getChangeValueLabel = (
  change: ProjectChange,
  version?: Baseline,
  options?: GetChangeValueLabelOptions,
): string | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementPropertyResolved: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      const valueStr = formatChangeNumericDisplayString({
        kind: 'property',
        name: change.propertyName,
        value: change.value,
        elementId: change.elementId,
        version,
      });
      return `${label} = ${valueStr}`;
    }
    case ProjectChangeType.ElementPropertyExpression: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      return `${label} = ${FORMULA_UPDATED_CHANGE_DISPLAY_VALUE}`;
    }
    case ProjectChangeType.ElementPropertyFormatted: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      const valueStr = formatPropertyFormattedDisplayValue(
        change.formatted,
        change.propertyName,
        change.elementId,
        version,
      );
      return `${label}: ${valueStr}`;
    }
    case ProjectChangeType.ElementPropertyValue: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      return change.overrideValue !== undefined
        ? `${label} = ${formatChangeNumericDisplayString({
            kind: 'property',
            name: change.propertyName,
            value: change.overrideValue,
            elementId: change.elementId,
            version,
          })}`
        : `${label} (cleared)`;
    }
    case ProjectChangeType.ElementQuantityResolved:
      return `${quantityDisplayName(change.quantityName)} = ${formatChangeNumericDisplayString(
        {
          kind: 'quantity',
          name: change.quantityName,
          value: change.value,
          elementId: change.elementId,
          version,
        },
      )}`;
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
      return `${quantityDisplayName(change.quantityName)} = ${FORMULA_UPDATED_CHANGE_DISPLAY_VALUE}`;
    case ProjectChangeType.ElementQuantityFormatted:
      return `${quantityDisplayName(change.quantityName)}: ${formatQuantityFormattedDisplayValue(
        change.formatted,
        change.quantityName,
        change.elementId,
        version,
      )}`;
    case ProjectChangeType.ElementQuantityValue:
      return change.overrideValue !== undefined
        ? `${quantityDisplayName(change.quantityName)} = ${formatChangeNumericDisplayString(
            {
              kind: 'quantity',
              name: change.quantityName,
              value: change.overrideValue,
              elementId: change.elementId,
              version,
            },
          )}`
        : `${quantityDisplayName(change.quantityName)} (cleared)`;
    case ProjectChangeType.ElementQuantityFallbackCount: {
      const label = quantityDisplayName(change.quantityName);
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.quantityName,
        elementId: change.elementId,
        version,
        payload: change.fallbackCount,
      });
      if (valueStr.length > 0) return `${label} (${valueStr} fallback)`;
      return `${label} (fallback count)`;
    }
    case ProjectChangeType.ElementPropertyFallbackCount: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.propertyName,
        elementId: change.elementId,
        version,
        payload: change.fallbackCount,
      });
      if (valueStr.length > 0) return `${label} (${valueStr} fallback)`;
      return `${label} (fallback count)`;
    }
    case ProjectChangeType.ElementPropertyCount: {
      const label = resolveKnownPropertyDisplayName(change.propertyName);
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.propertyName,
        elementId: change.elementId,
        version,
        payload: change.count,
      });
      if (valueStr.length > 0) return `${label} (${valueStr})`;
      return `${label} (count)`;
    }
    case ProjectChangeType.ElementQuantityCount: {
      const label = quantityDisplayName(change.quantityName);
      if (change.count === undefined) return `${label} (cleared)`;
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.quantityName,
        elementId: change.elementId,
        version,
        payload: change.count,
      });
      if (valueStr.length > 0) return `${label} = ${valueStr}`;
      return `${label} (count)`;
    }
    case ProjectChangeType.ElementQuantitySelect:
      return change.quantityName
        ? quantityDisplayName(change.quantityName)
        : 'auto';
    case ProjectChangeType.ElementName:
      return change.name;
    case ProjectChangeType.ElementCategory:
      return getElementCategoryById(change.categoryId)?.name;
    case ProjectChangeType.ElementRecipeApply:
      return change.recipeId;
    case ProjectChangeType.ElementPropertyRemove:
      return `${change.propertyIds.length} propert${change.propertyIds.length === 1 ? 'y' : 'ies'}`;
    case ProjectChangeType.ElementCategoryEnrich: {
      const adds = change.addProperties.length + change.addChildren.length;
      const removes =
        change.removePropertyNames.length + change.removeChildIds.length;
      const parts: string[] = [];
      if (adds) parts.push(`+${adds}`);
      if (removes) parts.push(`−${removes}`);
      return parts.length ? parts.join(' / ') : undefined;
    }
    case ProjectChangeType.ProductElementCountResolved:
      return String(change.value);
    case ProjectChangeType.ProductElementCountExpression:
      return change.expression;
    case ProjectChangeType.ProductElementCountFormatted:
      return change.formatted;
    case ProjectChangeType.ProductElementProductId:
    case ProjectChangeType.ProductElementEpd:
      return version
        ? getProductName(change.productId, version.products)
        : change.productId;
    case ProjectChangeType.ProductElementCarbonLevelOverride: {
      const slice = getCarbonProductOverrideUiSlice(
        change,
        version,
        options?.marketEstimates,
      );
      return slice?.rowValue;
    }
    case ProjectChangeType.ProductElementTransportOverride:
      return change.value !== undefined ? String(change.value) : undefined;
    case ProjectChangeType.ProductElementWasteOverride:
      return change.value !== undefined ? String(change.value) : undefined;
    case ProjectChangeType.VersionName:
      return change.name;
    default:
      return undefined;
  }
};

export type ActionChangeGroup = {
  /** Representative changes (one per unique change template). elementId is irrelevant for display. */
  changes: ProjectChange[];
  /** Sorted, unique element IDs these changes apply to */
  elementIds: string[];
};

/**
 * Priority order for display when multiple change types target the same
 * quantity or property name. Higher = preferred.
 */
const CHANGE_DISPLAY_PRIORITY: Partial<Record<ProjectChangeType, number>> = {
  [ProjectChangeType.ElementQuantityResolved]: 4,
  [ProjectChangeType.ElementQuantityValue]: 3,
  [ProjectChangeType.ElementQuantityExpression]: 2,
  [ProjectChangeType.ElementQuantityFallbackExpression]: 2,
  [ProjectChangeType.ElementQuantityFallbackCount]: 2,
  [ProjectChangeType.ElementQuantityFormatted]: 1,
  [ProjectChangeType.ElementPropertyResolved]: 4,
  [ProjectChangeType.ElementPropertyValue]: 3,
  [ProjectChangeType.ElementPropertyExpression]: 2,
  [ProjectChangeType.ElementPropertyFormatted]: 1,
};

/** Returns the quantity or property name targeted by a change, if applicable. */
const getChangeDisplayKey = (change: ProjectChange): string | undefined => {
  if ('quantityName' in change && change.quantityName)
    return `qty:${change.quantityName}`;
  if ('propertyName' in change && change.propertyName)
    return `prop:${(change as { propertyName: string }).propertyName}`;
  return undefined;
};

/**
 * Groups an action's {@link ProjectChange}s so that changes identical in every
 * field except `elementId` are collapsed into a single template entry, and
 * groups with the same set of element IDs are then merged together.
 *
 * Within each group, multiple change types targeting the same quantity or
 * property name are deduplicated — only the highest-priority type is kept
 * so the same row isn't shown more than once.
 *
 * The resulting groups render as: change rows first, element rows second.
 */
export const groupActionChanges = (
  changes: ProjectChange[],
): ActionChangeGroup[] => {
  const byFingerprint = new Map<
    string,
    { rep: ProjectChange; elementIds: Set<string> }
  >();
  for (const change of changes) {
    const elementId =
      'elementId' in change
        ? (change as { elementId: string }).elementId
        : undefined;
    const { elementId: _eid, ...rest } = change as Record<string, unknown>;
    const fingerprint = JSON.stringify(rest);
    if (!byFingerprint.has(fingerprint)) {
      byFingerprint.set(fingerprint, { rep: change, elementIds: new Set() });
    }
    const group = byFingerprint.get(fingerprint);
    if (elementId && group) group.elementIds.add(elementId);
  }

  const byElementSet = new Map<
    string,
    { changes: ProjectChange[]; elementIds: string[] }
  >();
  for (const { rep, elementIds } of byFingerprint.values()) {
    const sortedIds = [...elementIds].sort();
    const setKey = sortedIds.join('\0');
    if (!byElementSet.has(setKey)) {
      byElementSet.set(setKey, { changes: [], elementIds: sortedIds });
    }
    const group = byElementSet.get(setKey);
    if (group) group.changes.push(rep);
  }

  // Deduplicate: for each element-set group, keep only the highest-priority
  // change type per quantity/property display key.
  return [...byElementSet.values()].map((group) => {
    const best = new Map<string, ProjectChange>();
    const ungrouped: ProjectChange[] = [];
    for (const change of group.changes) {
      const key = getChangeDisplayKey(change);
      if (!key) {
        ungrouped.push(change);
        continue;
      }
      const existing = best.get(key);
      const newPriority = CHANGE_DISPLAY_PRIORITY[change.type] ?? 0;
      const existingPriority = existing
        ? (CHANGE_DISPLAY_PRIORITY[existing.type] ?? 0)
        : -1;
      if (newPriority > existingPriority) best.set(key, change);
    }
    return { ...group, changes: [...best.values(), ...ungrouped] };
  });
};

/**
 * One verb + one optional noun describing what a single {@link ProjectChange} does.
 * Used by {@link buildActionName} to group changes by verb (e.g. all "Set" entries collapse
 * into one segment `"width, thickness set"`).
 */
type ChangeDescription = {
  verb: string;
  noun?: string;
};

/** Returns "Increased", "Decreased", or "Changed" based on direction of numeric change. */
const directionVerb = (
  oldValue: number | undefined,
  newValue: number,
): 'Increased' | 'Decreased' | 'Changed' => {
  if (oldValue === undefined) return 'Changed';
  if (newValue > oldValue) return 'Increased';
  if (newValue < oldValue) return 'Decreased';
  return 'Changed';
};

/** Reads the cached resolved value of an element property from the version (before changes). */
const getOldPropertyResolved = (
  version: Baseline | undefined,
  elementId: string,
  propertyName: string,
): number | undefined => {
  const element = version
    ? getElementById(version, elementId, false)
    : undefined;
  const props = (
    element as
      | { properties?: Array<{ name: string; count?: { resolved?: number } }> }
      | undefined
  )?.properties;
  return props?.find((p) => p.name === propertyName)?.count?.resolved;
};

/** Reads the cached resolved value of an element quantity from the version (before changes). */
const getOldQuantityResolved = (
  version: Baseline | undefined,
  elementId: string,
  quantityName: string,
): number | undefined => {
  const element = version
    ? getElementById(version, elementId, false)
    : undefined;
  const qty = (
    element as
      | { quantity?: Record<string, { count?: { resolved?: number } }> }
      | undefined
  )?.quantity?.[quantityName];
  return qty?.count?.resolved;
};

/**
 * Maps a single change to a `(verb, noun)` description used for action naming.
 * The element-name resolver is injected so callers can avoid an extra closure when iterating.
 */
const describeChange = (
  change: ProjectChange,
  version: Baseline | undefined,
  resolveElementName: (id: string) => string | undefined,
): ChangeDescription | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementActivate:
      return { verb: 'Activate', noun: resolveElementName(change.elementId) };
    case ProjectChangeType.ElementDeactivate:
      return { verb: 'Deactivate', noun: resolveElementName(change.elementId) };
    case ProjectChangeType.ElementSoftDelete:
      return { verb: 'Delete', noun: resolveElementName(change.elementId) };
    case ProjectChangeType.ElementRemove:
      return { verb: 'Remove', noun: resolveElementName(change.elementId) };
    case ProjectChangeType.ElementName:
      return { verb: 'Rename', noun: change.name };
    case ProjectChangeType.ElementCategory:
      return {
        verb: 'Changed',
        noun: getElementCategoryById(change.categoryId)?.name ?? 'material',
      };
    case ProjectChangeType.ElementCategoryEnrich:
      return {
        verb: 'Enrich category',
        noun: resolveElementName(change.elementId),
      };
    case ProjectChangeType.ElementRecipeApply:
      return { verb: 'Applied', noun: 'recipe' };
    case ProjectChangeType.ElementRecipeDetach:
      return { verb: 'Detached', noun: 'recipe' };
    case ProjectChangeType.ElementRecipeClear:
      return { verb: 'Cleared', noun: 'recipe' };
    case ProjectChangeType.ElementQuantityResolved: {
      const oldVal = getOldQuantityResolved(
        version,
        change.elementId,
        change.quantityName,
      );
      return {
        verb:
          oldVal !== undefined ? directionVerb(oldVal, change.value) : 'Set',
        noun: quantityDisplayName(change.quantityName),
      };
    }
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression: {
      const oldVal = getOldQuantityResolved(
        version,
        change.elementId,
        change.quantityName,
      );
      const newVal = finiteNumberFromParsedQuantityText(change.expression);
      return {
        verb:
          oldVal !== undefined && newVal !== undefined
            ? directionVerb(oldVal, newVal)
            : 'Set',
        noun: quantityDisplayName(change.quantityName),
      };
    }
    case ProjectChangeType.ElementQuantityFallbackCount: {
      const oldVal = getOldQuantityResolved(
        version,
        change.elementId,
        change.quantityName,
      );
      const newVal = change.fallbackCount.resolved;
      return {
        verb:
          oldVal !== undefined && newVal !== undefined
            ? directionVerb(oldVal, newVal)
            : 'Set',
        noun: quantityDisplayName(change.quantityName),
      };
    }
    case ProjectChangeType.ElementQuantityFormatted: {
      const oldVal = getOldQuantityResolved(
        version,
        change.elementId,
        change.quantityName,
      );
      const newVal = finiteNumberFromParsedQuantityText(change.formatted);
      return {
        verb:
          oldVal !== undefined && newVal !== undefined
            ? directionVerb(oldVal, newVal)
            : 'Set',
        noun: quantityDisplayName(change.quantityName),
      };
    }
    case ProjectChangeType.ElementQuantityValue: {
      const oldVal = getOldQuantityResolved(
        version,
        change.elementId,
        change.quantityName,
      );
      const newVal = change.overrideValue;
      return {
        verb:
          oldVal !== undefined && newVal !== undefined
            ? directionVerb(oldVal, newVal)
            : 'Set',
        noun: quantityDisplayName(change.quantityName),
      };
    }
    case ProjectChangeType.ElementQuantitySelect:
      return {
        verb: 'Select quantity',
        noun: change.quantityName
          ? quantityDisplayName(change.quantityName)
          : 'auto',
      };
    case ProjectChangeType.ElementQuantityAutoUpdate:
      return {
        verb: 'Update quantity',
        noun: resolveElementName(change.elementId),
      };
    case ProjectChangeType.ElementPropertyResolved: {
      const noun =
        PROPERTY_NOUN_MAP[change.propertyName] ?? change.propertyName;
      if (PROPERTY_NOUN_MAP[change.propertyName] !== undefined) {
        const oldVal = getOldPropertyResolved(
          version,
          change.elementId,
          change.propertyName,
        );
        return {
          verb:
            oldVal !== undefined
              ? directionVerb(oldVal, change.value)
              : 'Changed',
          noun,
        };
      }
      return { verb: 'Set', noun };
    }
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementPropertyFormatted:
    case ProjectChangeType.ElementPropertyFallbackCount:
    case ProjectChangeType.ElementPropertyCount:
      return {
        verb: 'Set',
        noun: PROPERTY_NOUN_MAP[change.propertyName] ?? change.propertyName,
      };
    case ProjectChangeType.ElementPropertyValue: {
      const noun =
        PROPERTY_NOUN_MAP[change.propertyName] ?? change.propertyName;
      if (
        PROPERTY_NOUN_MAP[change.propertyName] !== undefined &&
        change.overrideValue !== undefined
      ) {
        const oldVal = getOldPropertyResolved(
          version,
          change.elementId,
          change.propertyName,
        );
        return {
          verb:
            oldVal !== undefined
              ? directionVerb(oldVal, change.overrideValue)
              : 'Changed',
          noun,
        };
      }
      return { verb: 'Set', noun };
    }
    case ProjectChangeType.ElementAdd:
      return { verb: 'Add', noun: change.factory.name ?? 'element' };
    case ProjectChangeType.ElementPropertyAdd:
      return { verb: 'Add property', noun: change.factory.name };
    case ProjectChangeType.ElementPropertyRemove: {
      const count = change.propertyIds.length;
      return {
        verb: 'Remove',
        noun: `${count} ${count === 1 ? 'property' : 'properties'}`,
      };
    }
    case ProjectChangeType.ElementDataTablePropertiesApply:
      return { verb: 'Apply data table', noun: change.dataTable.name };
    case ProjectChangeType.ProductElementProductId:
    case ProjectChangeType.ProductElementEpd:
      return { verb: 'Changed', noun: 'product' };
    case ProjectChangeType.ProductElementTransportOverride: {
      const newVal = change.value;
      const oldElement = version
        ? getElementById(version, change.elementId, false)
        : undefined;
      const oldVal = (
        oldElement as
          | {
              productOverrides?: {
                TRANSPORT_KM?: { override?: { value?: number } };
              };
            }
          | undefined
      )?.productOverrides?.[ProductElementOverrideTarget.TRANSPORT_KM]?.override
        ?.value;
      return {
        verb: newVal !== undefined ? directionVerb(oldVal, newVal) : 'Changed',
        noun: 'transport',
      };
    }
    case ProjectChangeType.ProductElementWasteOverride: {
      const newVal = change.value;
      const oldElement = version
        ? getElementById(version, change.elementId, false)
        : undefined;
      const oldVal = (
        oldElement as
          | {
              productOverrides?: {
                WASTE_PERCENT?: { override?: { value?: number } };
              };
            }
          | undefined
      )?.productOverrides?.[ProductElementOverrideTarget.WASTE_PERCENT]
        ?.override?.value;
      return {
        verb: newVal !== undefined ? directionVerb(oldVal, newVal) : 'Changed',
        noun: 'waste',
      };
    }
    case ProjectChangeType.ProductElementCarbonLevelOverride: {
      const newPercentile = change.percentile;
      const oldElement = version
        ? getElementById(version, change.elementId, false)
        : undefined;
      const oldPercentile = (
        oldElement as
          | {
              productOverrides?: {
                CO2E_A1_A3?: { override?: { value?: number } };
              };
            }
          | undefined
      )?.productOverrides?.[ProductElementOverrideTarget.CO2E_A1_A3]?.override
        ?.value;
      const effectiveOld = oldPercentile ?? 50;
      const effectiveNew = newPercentile ?? 50;
      return {
        verb: directionVerb(effectiveOld, effectiveNew),
        noun: 'carbon factor',
      };
    }
    case ProjectChangeType.ProductElementCountResolved:
    case ProjectChangeType.ProductElementCountExpression:
    case ProjectChangeType.ProductElementCountFormatted:
      return { verb: 'Set count', noun: resolveElementName(change.elementId) };
    case ProjectChangeType.VersionName:
      return { verb: 'Rename baseline', noun: change.name };
    default:
      // Fall back to the generic type label so we always produce something printable
      return { verb: getChangeTypeLabel(change.type) };
  }
};

/**
 * Maps {@link describeChange} verb labels to a natural trailing phrase after the noun list
 * (e.g. `"Top area increased"`).
 */
const TRAILING_ACTION_VERB: Record<string, string> = {
  Increased: 'increased',
  Decreased: 'decreased',
  Changed: 'changed',
  Set: 'set',
  Activate: 'activated',
  Deactivate: 'deactivated',
  Delete: 'deleted',
  Remove: 'removed',
  Rename: 'renamed',
  Add: 'added',
  'Add property': 'added',
  'Enrich category': 'category enriched',
  'Select quantity': 'selected',
  'Update quantity': 'updated',
  'Apply data table': 'applied',
  'Set count': 'count set',
  'Rename baseline': 'renamed',
  'Override product': 'overridden',
};

const trailingVerbPhraseForActionName = (verb: string): string =>
  TRAILING_ACTION_VERB[verb] ?? verb.toLowerCase();

/**
 * Builds a human-readable name for an action from its {@link ProjectChange}s.
 *
 * Uses {@link groupActionChanges} so redundant payloads for the same property or quantity
 * (e.g. resolved + formatted + value override) contribute only once — matching action rows.
 * Segment order follows the original {@link changes} order (first occurrence of each retained change).
 *
 * Each retained change is mapped to a `(verb, noun)` pair via {@link describeChange}; entries are
 * grouped by verb (deduplicated nouns). Each segment is **`{nouns} {trailingVerb}`** (e.g.
 * `"Top area increased"`), joined with ` · `.
 *
 * Examples:
 *  - `[Deactivate Metal]` → `"Metal deactivated"`
 *  - `[Set mass]` → `"Mass set"`
 *  - `[Set width, Set thickness]` → `"Width, thickness set"`
 *  - `[Activate Concrete, Set volume]` → `"Concrete activated · Volume set"`
 *
 * @param changes - The changes the action carries
 * @param version - Optional version used to resolve element/product names; falls back to ids
 *                  when the lookup fails so the result is always printable.
 * @returns The composed name, or `undefined` for an empty change list.
 */
export const buildActionName = (
  changes: ProjectChange[],
  version?: Baseline,
): string | undefined => {
  if (changes.length === 0) return undefined;

  const changesForNaming = groupActionChanges(changes)
    .flatMap((g) => g.changes)
    .sort((a, b) => changes.indexOf(a) - changes.indexOf(b));

  const resolveElementName = (id: string): string | undefined => {
    if (!version) return undefined;
    const element = getElementById(version, id, false);
    return element ? getElementName(element, version.products) : undefined;
  };

  const grouped = new Map<string, string[]>();
  // Insertion order of verbs is preserved by Map → segments come out in change order.
  for (const change of changesForNaming) {
    const desc = describeChange(change, version, resolveElementName);
    if (!desc) continue;
    const nouns = grouped.get(desc.verb) ?? [];
    if (desc.noun && !nouns.includes(desc.noun)) nouns.push(desc.noun);
    grouped.set(desc.verb, nouns);
  }

  if (grouped.size === 0) return undefined;

  const segments: string[] = [];
  for (const [verb, nouns] of grouped) {
    if (nouns.length === 0) {
      segments.push(verb);
      continue;
    }
    const trailing = trailingVerbPhraseForActionName(verb);
    const raw = `${nouns.join(', ')} ${trailing}`;
    segments.push(
      /^[a-z]/.test(raw) ? raw.replace(/^[a-z]/, (c) => c.toUpperCase()) : raw,
    );
  }
  return segments.join(' · ');
};
